import { useCallback, useEffect, useState } from 'preact/hooks';
import { revealStep, splitFresh, type LedgerProps, type LedgerState } from './ledger.model';

interface Options<T> {
  id: (row: T) => string;
  /** `loading` before the first read lands, `error` when it failed, else `ready`. */
  state: LedgerState;
  /** Rows revealed per step (default 50). */
  pageSize?: number;
  /** Auto-load stops here; the "Load more" button goes past it (default 200). */
  cap?: number;
  earliest?: string | undefined;
  /** See `LedgerProps.limited`. */
  limited?: boolean | undefined;
  /** See `LedgerProps.rowPx`. */
  rowPx?: number | undefined;
  /** See `LedgerProps.minWidthPx`. */
  minWidthPx?: number | undefined;
  /** Hold rows that arrive on a later read behind the "N new entries" pill (default).
   *  Pass `false` for a list that is not ordered newest-first (an inventory sorted
   *  by id): a new row lands in the middle, where "↑ new entries" would be a lie, so
   *  it is simply merged in. */
  holdNew?: boolean;
  onRetry?: (() => void) | undefined;
}

/** Drives a `Table`'s `ledger` prop over a list that is ALREADY fully in memory
 *  (a poll that returns every row): it paces how many rows are revealed and holds
 *  back rows that arrive while the reader is scrolled down.
 *
 *  `items` must be sorted newest-first — "new entries" are shown at the top.
 *
 *  // ponytail: reveal is client-side over a fully loaded list, so "loaded" means
 *  // "shown". A server-paged list (cursor) would feed `loaded`/`total` from its
 *  // pages instead; the `LedgerProps` shape does not change. */
export function useLedger<T>(items: T[], o: Options<T>) {
  const pageSize = o.pageSize ?? 50;
  const cap = o.cap ?? 200;
  const { id } = o;

  // Ids the reader has been shown. `null` until the first successful read, so the
  // first batch is never "new".
  const [known, setKnown] = useState<Set<string> | null>(null);
  const [n, setN] = useState(pageSize);
  /** Rows merged in from "new entries" — they sit ABOVE the revealed page and do
   *  not use up its budget. */
  const [added, setAdded] = useState(0);
  const [atTop, setAtTop] = useState(true);

  const ready = o.state === 'ready';
  useEffect(() => {
    if (known === null && ready) setKnown(new Set(items.map(id)));
  }, [known, ready]);

  const { shown, pending } = splitFresh(items, o.holdNew === false ? null : known, id);
  const total = shown.length;
  const loaded = Math.min(total, n + added);

  const showPending = useCallback(() => {
    setKnown(new Set(items.map(id)));
    setAdded((a) => a + pending);
  }, [items, pending]);

  // At the top there is nothing to jump to, so new rows simply appear; the pill is
  // only for a reader who is further down.
  useEffect(() => {
    if (pending > 0 && atTop) showPending();
  }, [pending, atTop]);

  const ledger: LedgerProps = {
    state: o.state,
    loaded,
    total,
    pending,
    paused: loaded < total && n >= cap,
    ended: total > 0 && loaded >= total,
    pageSize,
    earliest: o.earliest,
    limited: o.limited,
    rowPx: o.rowPx,
    minWidthPx: o.minWidthPx,
    onRetry: o.onRetry,
    onMore: () => setN((v) => v + pageSize),
    onShowPending: showPending,
    onEndReached: () => setN((v) => revealStep(v, added, total, pageSize, cap)),
    onAtTop: setAtTop,
    onSort: (active) => setN(active ? Number.MAX_SAFE_INTEGER : pageSize),
  };
  return { rows: shown.slice(0, loaded), ledger };
}

interface ServerOptions<T> {
  id: (row: T) => string;
  state: LedgerState;
  /** The server has a further page after the rows in `items`. */
  hasMore: boolean;
  fetchMore: () => void;
  /** That next page is in flight. */
  fetching: boolean;
  /** Rows per server page (the pause row's "Load N more"). */
  pageSize: number;
  /** Auto-load stops once this many rows are on screen (default 200). */
  cap?: number;
  rowPx?: number | undefined;
  /** See `LedgerProps.minWidthPx`. */
  minWidthPx?: number | undefined;
  onRetry?: (() => void) | undefined;
  /** Rows the server has that `items` does not yet hold — newer entries found by a
   *  background read. They wait behind the pill while the reader is scrolled down. */
  newRows?: T[];
  /** Put `newRows` into the list (the caller owns where they go). */
  onMergeNew?: (rows: T[]) => void;
}

/** `useLedger`'s sibling for a list the SERVER pages (a cursor, no total): every
 *  fetched row is already on screen, so "loaded" is `items.length`; scrolling to the
 *  bottom fetches the next page until the ceiling, then pauses behind a button.
 *
 *  `total` is only a floor while `hasMore` — the footer shows "90 of 90+". When the
 *  backend sends a real total, feed it here instead. */
export function useServerLedger<T>(items: T[], o: ServerOptions<T>) {
  const cap = o.cap ?? 200;
  const [atTop, setAtTop] = useState(true);
  const newRows = o.newRows ?? [];
  const pending = newRows.length;

  const merge = () => o.onMergeNew?.(newRows);
  // At the top there is nothing to jump to, so new rows simply appear.
  useEffect(() => {
    if (pending > 0 && atTop) merge();
  }, [pending, atTop]);

  const total = items.length;
  const ledger: LedgerProps = {
    state: o.state,
    loaded: total,
    total,
    pending,
    paused: o.hasMore && !o.fetching && total >= cap,
    ended: total > 0 && !o.hasMore,
    more: o.hasMore,
    fetching: o.fetching,
    pageSize: o.pageSize,
    rowPx: o.rowPx,
    minWidthPx: o.minWidthPx,
    onRetry: o.onRetry,
    onMore: () => {
      if (o.hasMore && !o.fetching) o.fetchMore();
    },
    onShowPending: merge,
    onEndReached: () => {
      if (o.hasMore && !o.fetching && total < cap) o.fetchMore();
    },
    onAtTop: setAtTop,
    onSort: () => undefined,
  };
  return { rows: items, ledger };
}
